import { create } from 'zustand'
import { devtools, subscribeWithSelector } from 'zustand/middleware'
import isEqual from 'lodash/isEqual'
import {
   OnboardingV2Service,
   revisionFrom,
} from '@forwardflow/services/onboardingV2.service'
import { ITier } from '@forwardflow/types/subscription'
import {
   IBusinessHours,
   IAppointmentTimeFrame,
   TOnboardingStep,
   IBusinessDetails,
   IStageMapGroup,
   TFubUser,
   ITempStageGroup,
   TStageGroupId,
   TStopStageKey,
   ICampaignConfiguration,
   IPond,
} from '@forwardflow/types/onboarding'
import {
   initialAvailabilityDays,
   initialCampaignConfiguration,
   initialStageMappingsGroups,
   initialTempStageGroups,
   ONBOARDING_STEPS,
   AGENT_ONBOARDING_STEPS,
} from '@forwardflow/constants/onboarding.constants'
import {
   transformStageMappingData,
   transformStageMappingDataToTempStageGroups,
} from '@forwardflow/utils/stage-mapping.utils'

/** What `initializeOnboardingData` actually did. See its doc comment. */
export type InitializeResult = 'loaded' | 'empty' | 'failed'

/** Why the autosave is suspended, or `null`. See `onboardingSaveBlocked`. */
export type SaveBlockedReason = 'conflict' | 'load-failed' | null

export interface IOnboardingData {
   email: string | null
   currentStep: TOnboardingStep
   organizationId: string | null
   isFUBConnected: boolean
   isSubscriptionActivated: boolean
   isCalendarConnected: boolean
   isOnBoardingCompleted: boolean
   inboxAppInstallRequest: boolean
   isCampaignConfigured: boolean
   selectedTier?: ITier | null
   planPeriod: 'monthly' | 'yearly'
   pricingData: {
      selectedTierId: number | null
      seatsCount: number
      newLeadBudget: number
      reactivateLeadBudget: number
      maxSpend: number
   }
   businessDetails: IBusinessDetails
   businessHours: IBusinessHours
   appointmentTimeframe: IAppointmentTimeFrame
   stageMappings: IStageMapGroup[]
   tempStageGroups: ITempStageGroup[]
   invitedUsers: TFubUser[]
   campaignConfiguration: ICampaignConfiguration
}

export type IStage = {
   id: string
   name: string
   description: string
   type?: 'stage' | 'tag'
}

export interface ITag {
   name: string
   description: string
   id: string
   type?: 'tag'
}

interface IOnboardingStoreV2 {
   onboardingData: IOnboardingData
   stages: IStage[]
   tags: string[]
   sources: string[]
   ponds: Array<IPond>
   setOnboardingData: (data: IOnboardingData) => void
   updateOnboardingData: (data: IOnboardingData) => void
   setCurrentStep: (step: TOnboardingStep) => void
   getCurrentStep: () => TOnboardingStep
   getNextStep: () => TOnboardingStep
   getPreviousStep: () => TOnboardingStep
   setOrganizationId: (organizationId: string) => void
   setIsFUBConnected: (isFUBConnected: boolean) => void
   setIsSubscriptionActivated: (isSubscriptionActivated: boolean) => void
   setIsCalendarConnected: (isCalendarConnected: boolean) => void
   setIsOnBoardingCompleted: (isOnBoardingCompleted: boolean) => void
   initializeOnboardingData: (
      organizationId: string
   ) => Promise<InitializeResult>
   /**
    * Plan 261 — state for an organization whose record is never read.
    *
    * The agent path is the one that takes it: it routes straight to calendar
    * connection without loading anything, so what is in the store still
    * belongs to the PREVIOUS organization. Fresh defaults under this
    * organization's id, at the step that path starts from, keeping the
    * signed-in identity — an agent with no address cannot be marked finished.
    *
    * Establishes NO basis, deliberately. Nothing was read, so there is no
    * revision to write against, and these defaults are a safe thing to SHOW,
    * never a record to save.
    */
   adoptEmptyRecordFor: (
      organizationId: string,
      currentStep: TOnboardingStep
   ) => void
   setEmail: (email: string) => void
   setSelectedTier: (selectedTier: Partial<ITier> | null) => void
   setPlanPeriod: (planPeriod: 'monthly' | 'yearly') => void
   setIsLoadingOrInitialization: (isLoading: boolean) => void
   isLoadingOrInitialization: boolean
   /**
    * Plan 261 — why this tab has stopped saving, or `null` when it has not.
    *
    * Two reasons, kept apart because they need different words and different
    * recovery. `conflict` means the setup was changed elsewhere and this
    * tab's basis is stale. `load-failed` means the record never loaded, so
    * there is no basis at all and what is on screen may be defaults — writing
    * those back would replace a real setup with an empty one.
    *
    * State rather than a log line: a customer whose edits silently stop
    * persisting needs telling, and a console message tells nobody.
    */
   onboardingSaveBlocked: SaveBlockedReason
   setOnboardingSaveBlocked: (reason: SaveBlockedReason) => void
   setBusinessHours: (businessHours: IBusinessHours) => void
   setAppointmentTimeframe: (
      appointmentTimeframe: IAppointmentTimeFrame
   ) => void
   setBusinessDetails: (businessDetails: IBusinessDetails) => void
   setStages: (stages: IStage[]) => void
   setStageMappings: (stageMappings: IStageMapGroup[]) => void
   setInvitedUsers: (invitedUsers: TFubUser[]) => void
   getUnselectedStages: () => IStage[]
   getUnSelectedTags: () => string[]
   goToStep: (step: TOnboardingStep) => void
   setTags: (tags: string[]) => void
   setSources: (sources: string[]) => void
   setPonds: (ponds: IPond[]) => void
   setInboxAppInstallRequest: (inboxAppInstallRequest: boolean) => void
   setTempStageGroupByGroupId: (
      groupId: TStageGroupId | TStopStageKey,
      group: ITempStageGroup
   ) => void
   setTempStagesByGroupId: (
      groupId: TStageGroupId | TStopStageKey,
      stagesOrTags: Array<IStage | ITag>
   ) => void
   getTempStagesByGroupId: (
      groupId: TStageGroupId | TStopStageKey
   ) => Array<IStage | ITag>
   setCampaignConfiguration: (
      campaignConfiguration: Partial<ICampaignConfiguration>
   ) => void
   setIsCampaignConfigured: (isCampaignConfigured: boolean) => void
   getFirstFilterCriteria: () => {
      field: string
      value: string[]
      logic: 'AND'
      operator: 'CONTAINS_ANY' | 'CONTAINS_ALL' | 'NOT_CONTAINS_ANY'
   } | null
   getRestFilterCriterias: () => {
      field: string
      value: string[]
      logic: 'AND'
      operator: 'CONTAINS_ANY' | 'CONTAINS_ALL' | 'NOT_CONTAINS_ANY'
   }[]
   setFirstFilterCriteria: (firstFilterCriteria: {
      field: string
      value: string[]
      logic: 'AND'
      operator: 'CONTAINS_ANY' | 'CONTAINS_ALL' | 'NOT_CONTAINS_ANY'
   }) => void
   transformStageMappingDataAndUpdate: () => void
   setPricingData: (pricingData: {
      selectedTierId: number | null
      seatsCount: number
      newLeadBudget: number
      reactivateLeadBudget: number
      maxSpend: number
   }) => void
   isAgent: boolean
   setIsAgent: (isAgent: boolean) => void
}

const isStageSelected = (stage: IStage, tempStageGroups: ITempStageGroup[]) => {
   // Check in regular stages
   const isInRegularStages = tempStageGroups.some((group) =>
      group.stageOrTags?.some(
         (item) => String(item.id) === String(stage.id) && item.type === 'stage'
      )
   )
   if (isInRegularStages) return true

   // Check in types (for stop stages)
   const isInTypes = tempStageGroups.some(
      (group) =>
         group.type === 'appointment-field' &&
         group.stageOrTags?.some(
            (item) =>
               String(item.id) === String(stage.id) && item.type === 'stage'
         )
   )
   if (isInTypes) return true

   return false
}

const isTagSelected = (tag: string, tempStageGroups: ITempStageGroup[]) => {
   return tempStageGroups.some((group) =>
      group.stageOrTags?.some(
         (item) => String(item.name) === String(tag) && item.type === 'tag'
      )
   )
}

/**
 * The defaults a brand-new setup record starts from.
 *
 * Extracted so "this organization has no record" and "the store is starting
 * up" produce the SAME thing. Reusing whatever the store was holding and just
 * changing its `organizationId` is how one workspace's answers ended up
 * labelled as another's.
 */
const initialOnboardingData = (): IOnboardingData => ({
            email: null,
            currentStep: 'plan-selection',
            organizationId: null,
            isFUBConnected: false,
            isSubscriptionActivated: false,
            isCalendarConnected: false,
            selectedTier: null,
            planPeriod: 'monthly',
            businessDetails: {
               businessOwnerName: '',
               businessOwnerTitle: '',
               legalBusinessName: '',
               friendlyBusinessName: '',
               introductionTemplate: '',
            },
            pricingData: {
               seatsCount: 1,
               newLeadBudget: 93,
               reactivateLeadBudget: 57,
               maxSpend: 150,
               selectedTierId: null,
            },
            businessHours: {
               timezone: '',
               allowAgentsToSetAvailability: false,
               days: initialAvailabilityDays,
               isSameForAllDays: true,
            },
            appointmentTimeframe: {
               timezone: '',
               appointmentBufferTime: 0,
               appointmentType: 'virtual',
               appointmentLocationType: 'in-office',
               meetingPlatform: 'zoom',
               meetingLength: 0,
               meetingLink: '',
               allowAgentsToSetAvailability: false,
               days: initialAvailabilityDays,
               isSameForAllDays: true,
            },
            isOnBoardingCompleted: false,
            inboxAppInstallRequest: false,
            isCampaignConfigured: false,
            stageMappings: initialStageMappingsGroups,
            tempStageGroups: initialTempStageGroups,
            invitedUsers: [],
            campaignConfiguration: initialCampaignConfiguration,
         })

/**
 * The SIGNED-IN user's address, kept outside the record.
 *
 * `onboardingData.email` is part of the record, so every replacement of the
 * record drops it — and the agent path, which replaces the record and never
 * loads one, then had no identity to complete onboarding with. The loader's
 * email effect does not restore it: its dependency has not changed.
 *
 * Copying the address out of the record being discarded would be the wrong
 * repair. A loaded record carries whatever the backend stored, which may be
 * the owner who set the workspace up rather than the person using it.
 * Identity is not one of the workspace's answers, so it is held separately,
 * written only by `setEmail`, and re-seeded into every fresh record.
 */
let authenticatedEmail: string | null = null

/** Defaults for a workspace, keeping the signed-in identity. */
const freshRecordFor = (organizationId: string | null): IOnboardingData => ({
   ...initialOnboardingData(),
   organizationId,
   email: authenticatedEmail,
})

export const useOnboardingStoreV2 = create<IOnboardingStoreV2>()(
   subscribeWithSelector(
      devtools((set, get) => ({
         onboardingSaveBlocked: null,
         setOnboardingSaveBlocked: (onboardingSaveBlocked: SaveBlockedReason) =>
            set({ onboardingSaveBlocked }),
         onboardingData: initialOnboardingData(),
         setInboxAppInstallRequest: (inboxAppInstallRequest: boolean) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  inboxAppInstallRequest,
               },
            })),
         setCampaignConfiguration: (
            campaignConfiguration: Partial<ICampaignConfiguration>
         ) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  campaignConfiguration: {
                     ...state.onboardingData.campaignConfiguration,
                     ...campaignConfiguration,
                  },
               },
            })),
         getFirstFilterCriteria: () => {
            const { campaignConfiguration } = get().onboardingData
            const criteria =
               campaignConfiguration?.campaign_filters?.eligible_criteria?.[0]
            if (!criteria) return null
            // Ensure the value is always a string[]
            return {
               ...criteria,
               value: Array.isArray(criteria.value)
                  ? criteria.value.map(String)
                  : [],
            }
         },
         getRestFilterCriterias: () => {
            const { campaignConfiguration } = get().onboardingData
            const criterias =
               campaignConfiguration?.campaign_filters?.eligible_criteria?.slice(
                  1
               )
            if (!criterias) return []
            return criterias.map((criteria) => ({
               ...criteria,
               value: Array.isArray(criteria.value)
                  ? criteria.value.map(String)
                  : [],
            }))
         },
         setFirstFilterCriteria: (firstFilterCriteria: {
            field: string
            value: string[]
            logic: 'AND'
            operator: 'CONTAINS_ANY' | 'CONTAINS_ALL' | 'NOT_CONTAINS_ANY'
         }) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  campaignConfiguration: {
                     ...state.onboardingData.campaignConfiguration,
                     campaign_filters: {
                        ...state.onboardingData.campaignConfiguration
                           ?.campaign_filters,
                        eligible_criteria: [
                           {
                              field: firstFilterCriteria.field as
                                 | 'source'
                                 | 'stage'
                                 | 'tags'
                                 | 'assigned_pond_id'
                                 | 'inquired_property',
                              value: firstFilterCriteria.value,
                              logic: firstFilterCriteria.logic,
                              operator: firstFilterCriteria.operator,
                           },
                           ...(state.onboardingData.campaignConfiguration?.campaign_filters?.eligible_criteria?.slice(
                              1
                           ) ?? []),
                        ],
                     },
                  },
               },
            })),
         setIsCampaignConfigured: (isCampaignConfigured: boolean) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  isCampaignConfigured,
               },
            })),
         setInvitedUsers: (invitedUsers: TFubUser[]) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, invitedUsers },
            })),
         getUnselectedStages: () => {
            const { stages, onboardingData } = get()
            const { tempStageGroups } = onboardingData

            if (!stages || !tempStageGroups) return []

            return stages.filter(
               (stage) => !isStageSelected(stage, tempStageGroups)
            )
         },
         getUnSelectedTags: () => {
            const { tags, onboardingData } = get()
            const { tempStageGroups } = onboardingData

            if (!tags || !tempStageGroups) return []

            return tags.filter((tag) => !isTagSelected(tag, tempStageGroups))
         },
         setStageMappings: (stageMappings: IStageMapGroup[]) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, stageMappings },
            })),
         isAgent: false,
         setIsAgent: (isAgent: boolean) => set({ isAgent }),
         stages: [],
         tags: [],
         sources: [],
         ponds: [],
         setStages: (stages: IStage[]) => set({ stages }),
         setTags: (tags: string[]) => set({ tags }),
         setSources: (sources: string[]) => set({ sources }),
         setPonds: (ponds: IPond[]) => set({ ponds }),
         isLoadingOrInitialization: true,
         goToStep: (step: TOnboardingStep) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, currentStep: step },
            })),
         setPricingData: (pricingData: {
            seatsCount: number
            newLeadBudget: number
            reactivateLeadBudget: number
            maxSpend: number
         }) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  pricingData: {
                     ...state.onboardingData.pricingData,
                     ...pricingData,
                  },
               },
            })),
         setBusinessDetails: (businessDetails: IBusinessDetails) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, businessDetails },
            })),
         setOnboardingData: (data) => set({ onboardingData: data }),
         setOrganizationId: (organizationId: string) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, organizationId },
            })),
         updateOnboardingData: (data: Partial<IOnboardingData>) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, ...data },
            })),
         setCurrentStep: (step: TOnboardingStep) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, currentStep: step },
            })),
         getCurrentStep: () => get().onboardingData.currentStep,
         setTempStagesByGroupId: (
            groupId: TStageGroupId | TStopStageKey,
            stagesOrTags: Array<IStage | ITag>
         ) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  tempStageGroups: state.onboardingData.tempStageGroups.map(
                     (group) =>
                        group.groupId === groupId
                           ? { ...group, stageOrTags: stagesOrTags }
                           : group
                  ),
               },
            })),
         setTempStageGroupByGroupId: (
            groupId: TStageGroupId | TStopStageKey,
            group: ITempStageGroup
         ) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  tempStageGroups: state.onboardingData.tempStageGroups.map(
                     (g) => (g.groupId === groupId ? group : g)
                  ),
               },
            })),
         getTempStagesByGroupId: (groupId: TStageGroupId | TStopStageKey) => {
            const { tempStageGroups } = get().onboardingData
            const group = tempStageGroups?.find(
               (group) => group.groupId === groupId
            )
            if (!group) return []
            return group.stageOrTags
         },
         getNextStep: () => {
            const currentStep = get().getCurrentStep()
            const { isFUBConnected, isSubscriptionActivated, isCalendarConnected } =
               get().onboardingData
            const isAgent = get().isAgent

            if (isAgent) {
               const steps = AGENT_ONBOARDING_STEPS
               const index = steps.indexOf(currentStep)
               if (index === steps.length - 1) {
                  return currentStep
               }
               const nextStep = steps[index + 1]
               set((state) => ({
                  onboardingData: {
                     ...state.onboardingData,
                     currentStep: nextStep,
                  },
               }))
               return nextStep
            }

            const index = ONBOARDING_STEPS.indexOf(currentStep)

            if (index === ONBOARDING_STEPS.length - 1) {
               return currentStep
            }

            let nextIndex = index + 1
            let nextStep = ONBOARDING_STEPS[nextIndex]

            // Skip all first three steps if subscription is already activated
            if (
               isSubscriptionActivated &&
               ['plan-selection', 'fub-connect', 'payment-details'].includes(
                  nextStep
               )
            ) {
               while (
                  nextIndex < ONBOARDING_STEPS.length &&
                  ['plan-selection', 'fub-connect', 'payment-details'].includes(
                     ONBOARDING_STEPS[nextIndex]
                  )
               ) {
                  nextIndex++
               }
               nextStep = ONBOARDING_STEPS[nextIndex]
            }

            // Skip fub-connect step if FUB is already connected
            if (
               !isSubscriptionActivated &&
               isFUBConnected &&
               nextStep === 'fub-connect'
            ) {
               nextIndex++
               nextStep = ONBOARDING_STEPS[nextIndex]
            }

            if (isCalendarConnected && nextStep === 'calendar-connection') {
               nextIndex++
               nextStep = ONBOARDING_STEPS[nextIndex]
            }

            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  currentStep: nextStep,
               },
            }))
            return nextStep
         },
         setBusinessHours: (businessHours: IBusinessHours) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, businessHours },
            })),
         setAppointmentTimeframe: (
            appointmentTimeframe: IAppointmentTimeFrame
         ) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  appointmentTimeframe,
               },
            })),
         getPreviousStep: () => {
            const currentStep = get().getCurrentStep()
            const { isFUBConnected, isSubscriptionActivated, isCalendarConnected } =
               get().onboardingData
            const isAgent = get().isAgent

            if (isAgent) {
               const steps = AGENT_ONBOARDING_STEPS
               const index = steps.indexOf(currentStep)
               if (index === 0) {
                  return currentStep
               }
               const previousStep = steps[index - 1]
               set((state) => ({
                  onboardingData: {
                     ...state.onboardingData,
                     currentStep: previousStep,
                  },
               }))
               return previousStep
            }

            const index = ONBOARDING_STEPS.indexOf(currentStep)

            if (index === 0) {
               return currentStep
            }

            let previousIndex = index - 1
            let previousStep = ONBOARDING_STEPS[previousIndex]

            if (isCalendarConnected && previousStep === 'calendar-connection') {
               previousIndex--
               previousStep = ONBOARDING_STEPS[previousIndex]
            }

            // Skip fub-connect step if FUB is already connected
            if (
               !isSubscriptionActivated &&
               isFUBConnected &&
               previousStep === 'fub-connect'
            ) {
               previousIndex--
               previousStep = ONBOARDING_STEPS[previousIndex]
            }

            // Skip all first three steps if subscription is already activated
            if (
               isSubscriptionActivated &&
               ['plan-selection', 'fub-connect', 'payment-details'].includes(
                  previousStep
               )
            ) {
               while (
                  previousIndex >= 0 &&
                  ['plan-selection', 'fub-connect', 'payment-details'].includes(
                     ONBOARDING_STEPS[previousIndex]
                  )
               ) {
                  previousIndex--
               }
               // If we've gone past the beginning, stay at the current step
               if (previousIndex < 0) {
                  return currentStep
               }
               previousStep = ONBOARDING_STEPS[previousIndex]
            }

            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  currentStep: previousStep,
               },
            }))
            return previousStep
         },
         setIsFUBConnected: (isFUBConnected: boolean) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, isFUBConnected },
            })),
         setIsSubscriptionActivated: (isSubscriptionActivated: boolean) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  isSubscriptionActivated,
               },
            })),
         setIsCalendarConnected: (isCalendarConnected: boolean) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  isCalendarConnected,
               },
            })),
         setIsOnBoardingCompleted: (isOnBoardingCompleted: boolean) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  isOnBoardingCompleted,
               },
            })),
         setEmail: (email: string) => {
            // The record's copy AND the identity a fresh record is seeded
            // from. The loader calls this with the authenticated user's
            // address and is its only caller.
            authenticatedEmail = email
            set((state) => ({
               onboardingData: { ...state.onboardingData, email },
            }))
         },
         setSelectedTier: (selectedTier: Partial<ITier> | null) =>
            set(
               (state) =>
                  ({
                     onboardingData: {
                        ...state.onboardingData,
                        selectedTier: {
                           ...state.onboardingData.selectedTier,
                           ...selectedTier,
                        },
                     },
                  } as IOnboardingStoreV2)
            ),
         setPlanPeriod: (planPeriod: 'monthly' | 'yearly') =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, planPeriod },
            })),
         setIsLoadingOrInitialization: (isLoadingOrInitialization: boolean) =>
            set({ isLoadingOrInitialization }),
         transformStageMappingDataAndUpdate: () => {
            const { tempStageGroups } = get().onboardingData
            const updatedTempStageGroups =
               transformStageMappingData(tempStageGroups)
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  stageMappings: updatedTempStageGroups,
               },
            }))
         },
         /**
          * Load one organization's setup record.
          *
          * RETURNS WHAT HAPPENED. It used to swallow every failure, keep
          * whatever record was already in the store, change its
          * `organizationId` to the new one and resolve successfully — so a
          * caller could not tell a loaded record from another workspace's
          * answers relabelled, and a page that gated on the promise rejecting
          * never gated on anything. A payment page is not a place to find that
          * out.
          *
          *   'loaded' — this organization's record is in the store.
          *   'empty'  — it has none; the store holds fresh defaults, never
          *              the previous organization's answers.
          *   'failed' — the read failed. Nothing was adopted.
          */
         initializeOnboardingData: async (
            organizationId: string
         ): Promise<InitializeResult> => {
            /*
             * Cancel any scheduled write BEFORE anything else.
             *
             * The basis is reset to `revision: null` on the next line. A
             * pending timer firing between here and the end of the load would
             * send the pre-load record with no revision — the unchecked
             * compatibility path — and clearing the timer only when the load
             * finished left exactly that window open on a slow load.
             */
            cancelScheduledWrite()
            // This load's generation. Anything still in flight from an
            // earlier one is obsolete from here on.
            const generation = ++loadGeneration
            basis = { organizationId, generation, revision: null }
            // A new load is a new basis; any earlier refusal no longer applies.
            set({
               isLoadingOrInitialization: true,
               onboardingSaveBlocked: null,
            })

            /** Never carry one workspace's ANSWERS into another's id. The
             * signed-in identity is not one of them and is kept. */
            const freshFor = (id: string): IOnboardingData => freshRecordFor(id)
            const holdsAnotherOrg = () => {
               const held = get().onboardingData.organizationId
               return Boolean(held) && held !== organizationId
            }

            try {
               const response = await OnboardingV2Service.getOnboardingState({
                  organizationId,
               })
               // A newer load started while this one was in flight; its
               // answer is the current one, and this must not overwrite it.
               if (generation !== loadGeneration) return 'failed'
               // The revision this tab is now based on. Every later autosave
               // compares against it.
               basis = {
                  organizationId,
                  generation,
                  revision: revisionFrom(response),
               }

               const updatedTempStageGroups =
                  transformStageMappingDataToTempStageGroups(
                     response?.data?.stageMappings || []
                  )

               if (response?.data) {
                  // The record FIRST, while the loading flag still holds the
                  // subscriber off; the flag is cleared separately below.
                  set({
                     onboardingData: {
                        ...response.data, // Always use our local steps definition
                        // Older assistant records used the handoff purpose as a step.
                        // Normalize only the known alias while loading, never autosave it.
                        currentStep: (response.data.currentStep as string) === 'payment'
                           ? 'payment-details'
                           : response.data.currentStep,
                        organizationId,
                        tempStageGroups: updatedTempStageGroups,
                     },
                  })
                  settleAfterLoad()
                  return 'loaded'
               }
               // No record yet. Defaults for THIS organization, not whatever
               // the store happened to be holding.
               set({ onboardingData: freshFor(organizationId) })
               settleAfterLoad()
               return 'empty'
            } catch (error) {
               console.error('Failed to initialize onboarding data:', error)
               if (generation !== loadGeneration) return 'failed'
               /*
                * No revision, so nothing may be written on this basis — and
                * the defaults installed below must never reach the server.
                * They are a safe thing to SHOW, not a record to save: an
                * unchecked whole-record PATCH of them would replace a real
                * setup with an empty one.
                */
               basis = { organizationId, generation, revision: null }
               set((state) => ({
                  // Same organization: leave the customer's work alone. A
                  // different one: replace it, because relabelling it is how
                  // another workspace's pricing ends up on a payment page.
                  onboardingData: holdsAnotherOrg()
                     ? freshFor(organizationId)
                     : { ...state.onboardingData, organizationId },
               }))
               settleAfterLoad()
               // A load that failed is not a basis to write from, and the
               // customer is told which problem this is.
               set({ onboardingSaveBlocked: 'load-failed' })
               return 'failed'
            }
         },
         adoptEmptyRecordFor: (
            organizationId: string,
            currentStep: TOnboardingStep
         ) => {
            /*
             * No read, so no basis — and abandoning the load is what says so.
             *
             * Both halves of the previous behaviour were defects. The record
             * left in the store belonged to the organization just switched
             * AWAY from, so one workspace's answers showed under another's
             * id; and the basis left beside it was still that workspace's, so
             * the step change below went back to it as an ordinary autosave.
             */
            abandonActiveLoad()
            /*
             * Written while the loading flag holds the autosave subscriber
             * off, and cleared separately afterwards. Same sequencing a load
             * uses, for the same reason: a single `set` that did both would
             * leave the flag already false when the subscriber ran.
             */
            set({ isLoadingOrInitialization: true })
            set({
               onboardingData: {
                  ...freshRecordFor(organizationId),
                  currentStep,
               },
            })
            settleAfterLoad()
         },
      }))
   )
)

// Auto-sync onboardingData to backend whenever it changes
let syncTimeout: NodeJS.Timeout | null = null

/**
 * The setup revision this tab last saw — WHOSE, and FROM WHICH LOAD.
 *
 * A bare number was wrong twice over. Start saving workspace A, switch to B,
 * and A's response arriving later replaced B's revision with A's; a late 409
 * from A suspended B. Either produces a false conflict, or lets a stale write
 * through when the two numbers happen to coincide.
 *
 * So the revision carries the organization it belongs to and the load that
 * produced it. `generation` increments on every load, and any response from
 * an older one is obsolete and ignored.
 */
interface RevisionBasis {
   organizationId: string | null
   generation: number
   revision: number | null
}
let loadGeneration = 0
let basis: RevisionBasis = {
   organizationId: null,
   generation: 0,
   revision: null,
}

/**
 * Abandon whatever load is in flight, without starting another.
 *
 * `loadGeneration` is bumped by STARTING a load, which is enough while every
 * selection ends in one. It is not: selecting an organization whose user is
 * an agent routes away and never enters the store, so a load already in
 * flight for the PREVIOUS organization kept a matching generation and
 * committed its record over the one now selected. Unmounting during a load
 * had the same hole.
 *
 * So the hook's selection fence calls this, and a selection change invalidates
 * the store load explicitly rather than relying on a replacement load to do
 * it. The loading flag is deliberately left alone: whoever selected next owns
 * it, and clearing it here would switch off the guard for THEIR load.
 */
export const abandonActiveLoad = (): void => {
   loadGeneration += 1
   /*
    * And the WRITE basis, not only the read.
    *
    * Bumping the generation stops an in-flight load from committing, but the
    * basis left behind still named the previous organization and its
    * revision, and nothing compared the two. So an edit made after the switch
    * passed every guard in `runWrite` and saved to the workspace the customer
    * had just left — the agent path did exactly that, moving its own step
    * into the previous organization's record.
    *
    * No organization holds write authority until a load establishes one. The
    * generation moves with it so a response from the abandoned load is
    * obsolete on both counts.
    */
   basis = { organizationId: null, generation: loadGeneration, revision: null }
   cancelScheduledWrite()
}

/** The revision to send for this organization, or null if we hold none. */
const revisionFor = (organizationId: string): number | null =>
   basis.organizationId === organizationId ? basis.revision : null

/** Is this response still the current load for this organization? */
const stillCurrent = (organizationId: string, generation: number): boolean =>
   basis.generation === generation && basis.organizationId === organizationId

/**
 * Initialization must never trigger the autosave.
 *
 * The subscriber watches `onboardingData` and skips while
 * `isLoadingOrInitialization` is true — but a single `set` that wrote the
 * record AND cleared the flag left the flag already false by the time the
 * subscriber ran. On a failed load that scheduled an unchecked whole-record
 * PATCH of fresh defaults, which would replace a real record with an empty
 * one. Writing the record while the flag is still true, and clearing it in a
 * separate `set` the subscriber does not watch, is what keeps loads silent.
 */
const settleAfterLoad = () => {
   // Again at the end: the record was just replaced, and the subscriber will
   // have scheduled a write for it.
   cancelScheduledWrite()
   useOnboardingStoreV2.setState({ isLoadingOrInitialization: false })
}
/**
 * The autosave, serialized.
 *
 * Debouncing is not serialization. If one save took longer than the debounce,
 * a second edit launched a second save carrying the SAME revision: one won,
 * the other came back 409, and the tab conflicted with itself and stopped
 * saving while nobody else had touched anything. So exactly one write is in
 * flight at a time, and an edit that arrives during one is remembered and
 * written immediately afterwards, against the revision that save returned.
 */
let saveInFlight = false
let editedDuringSave = false
let lastSavedRecord: IOnboardingData | null = null

/** External navigation must not discard the debounced setup edit. */
export const flushOnboardingBeforeRedirect = async () => {
   const initial = useOnboardingStoreV2.getState()
   const generation = basis.generation
   const deadline = Date.now() + 15000
   while (saveInFlight) {
      if (Date.now() >= deadline) throw new Error('Setup save is still pending')
      await new Promise(resolve => setTimeout(resolve, 50))
   }
   if (generation !== basis.generation || initial.onboardingData.organizationId !== basis.organizationId) throw new Error('Workspace changed')
   cancelScheduledWrite()
   lastSavedRecord = null
   await runWrite()
   const current = useOnboardingStoreV2.getState()
   if (generation !== basis.generation || current.onboardingSaveBlocked || lastSavedRecord !== current.onboardingData) throw new Error('Setup could not be saved')
}
/**
 * Discard queued work: the debounced timer AND the "an edit arrived while a
 * save was running" flag. Both mean the same thing — a write that has not
 * happened yet — and leaving the flag set across a load or a conflict makes
 * the next completed save fire one more write nobody asked for.
 */
const cancelScheduledWrite = () => {
   if (syncTimeout) {
      clearTimeout(syncTimeout)
      syncTimeout = null
   }
   editedDuringSave = false
}

/**
 * Write the CURRENT record, if writing is still the right thing to do.
 *
 * Every guard is re-checked here rather than when the write was scheduled.
 * A load can start during the debounce, and the old checks ran either before
 * the wait or after the response — both too late to stop the request. A
 * pending timer firing mid-load would have sent the pre-load record with no
 * revision, straight down the unchecked compatibility path.
 */
const runWrite = async (): Promise<void> => {
   const state = useOnboardingStoreV2.getState()
   const record = state.onboardingData
   const organizationId = record.organizationId
   if (!organizationId) return
   // A load is running: its answer, not this record, is what should persist.
   if (state.isLoadingOrInitialization) return
   if (state.onboardingSaveBlocked) return
   // The basis moved — a different workspace, or a newer load of this one.
   if (basis.organizationId !== organizationId) return
   const generation = basis.generation

   if (saveInFlight) {
      // One at a time. The save that is running will pick this up.
      editedDuringSave = true
      return
   }
   saveInFlight = true
   /*
    * An obsolete response must not STRAND the queue.
    *
    * A stale answer used to `return`, which released the in-flight slot in
    * `finally` but skipped the drain below — so an edit made to the workspace
    * this tab had moved to sat unsaved until some later keystroke happened to
    * schedule work of its own. Nothing here returns early any more: obsolete
    * only means "adopt nothing from this answer", and the drain runs either
    * way because the queued edit belongs to the CURRENT workspace, which
    * `runWrite` re-checks from scratch.
    */
   try {
      const response = await OnboardingV2Service.updateOnboardingState({
         organizationId,
         data: { ...record },
         revision: revisionFor(organizationId),
      })
      // Obsolete: a later load replaced the basis while this was in flight.
      // Adopting its revision here would stamp one workspace's number onto
      // another's.
      if (stillCurrent(organizationId, generation)) {
         const next = revisionFrom(response)
         if (next !== null) basis = { ...basis, revision: next }
         lastSavedRecord = record
      }
   } catch (error) {
      if (
         (error as { response?: { status?: number } })?.response?.status === 409
      ) {
         // Same reasoning: a late refusal from a workspace this tab has moved
         // on from must not suspend the one it is showing now — but it must
         // not swallow that workspace's pending edit either.
         if (stillCurrent(organizationId, generation)) {
            // Stop, and say so where someone can see it. Retrying would walk
            // into the same refusal, and overwriting is the thing the
            // revision exists to prevent.
            useOnboardingStoreV2.getState().setOnboardingSaveBlocked('conflict')
         }
      } else {
         console.error('Failed to sync onboarding data:', error)
      }
   } finally {
      saveInFlight = false
   }

   if (editedDuringSave) {
      /*
       * Edits made while that was in flight, written against the revision it
       * just returned rather than the one it consumed.
       *
       * `runWrite` re-reads the record and re-checks every guard, which is
       * what makes this safe in the two awkward cases: after an OBSOLETE
       * response it writes the current workspace's edit rather than the stale
       * one's, and after a CONFLICT it returns immediately because the block
       * is one of the guards. Neither needs handling here, and a separate
       * branch for either would be code no test could distinguish.
       */
      editedDuringSave = false
      await runWrite()
   }
}

useOnboardingStoreV2.subscribe(
   (state) => state.onboardingData,
   (onboardingData: IOnboardingData) => {
      const { isLoadingOrInitialization: isLoading, onboardingSaveBlocked } =
         useOnboardingStoreV2.getState()

      // Don't sync if loading (to avoid race conditions during initialization)
      if (isLoading) {
         return
      }

      /*
       * Plan 261 — once a save has been refused, STOP.
       *
       * Every later edit would schedule a write against a revision the server
       * has already rejected, so the customer would keep typing into a tab
       * that silently saves nothing. Writes resume when the record is
       * reloaded, which is what clears the flag.
       */
      if (onboardingSaveBlocked) {
         return
      }

      // Only sync if we have an organizationId
      if (!onboardingData.organizationId) {
         return
      }

      // Debounce the sync to avoid too many API calls
      cancelScheduledWrite()
      syncTimeout = setTimeout(() => {
         syncTimeout = null
         void runWrite()
      }, 1000)
   },
   {
      equalityFn: (a: IOnboardingData, b: IOnboardingData) => isEqual(a, b),
   }
)
